import { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import AdminNodeStore from '../../../stores/admin-node-store';
import { DbNode, NodeType } from '../../../types/node';
import { ILocation, Point } from '../../../types/location';
import { PositionService } from '../../../http/position-service';
import NodeLocationForm, { NODE_TYPE_LABELS, SELECTABLE_TYPES } from './NodeLocationForm';

interface EditNodeDialogProps {
    store: AdminNodeStore;
    node: DbNode;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const EditNodeDialog = ({ store, node, open, onClose, onSaved }: EditNodeDialogProps) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<NodeType>(NodeType.Department);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [address, setAddress] = useState('');
    const [coordinates, setCoordinates] = useState<Point | null>(null);
    const [locationDirty, setLocationDirty] = useState(false);
    const [centerTrigger, setCenterTrigger] = useState(0);

    useEffect(() => {
        if (!open) return;
        setName(node.name);
        setType(node.type);
        setDescription(node.description ?? '');
        setAddress('');
        setCoordinates(null);
        setError('');
        setLocationDirty(false);
        setCenterTrigger(0);

        PositionService.getByNode(node.node_id)
            .then((answer) => {
                const loc = answer.data as ILocation;
                setAddress(loc.address);
                setCoordinates(loc.coordinates);
                setCenterTrigger((t) => t + 1);
            })
            .catch(() => {
                // no location yet — that's fine
            });
    }, [open, node.node_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        if (loading) return;
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('Введите название');
            return;
        }
        setLoading(true);
        setError('');
        const updatedNode: DbNode = {
            ...node,
            name: name.trim(),
            type,
            description: description.trim() || null,
        };
        try {
            await store.updateNode(updatedNode, address, coordinates, locationDirty);
            onSaved();
            handleClose();
        } catch {
            setError('Не удалось сохранить');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !loading) handleSubmit(); }}
        >
            <DialogTitle>Редактировать</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField
                    label="Название"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!error && !name.trim()}
                    helperText={!name.trim() ? error : ''}
                    autoFocus
                    fullWidth
                    size="small"
                />
                <FormControl fullWidth size="small">
                    <InputLabel>Тип</InputLabel>
                    <Select label="Тип" value={type} onChange={(e) => setType(e.target.value as NodeType)}>
                        {SELECTABLE_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {NODE_TYPE_LABELS[t]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Описание (необязательно)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                />
                <NodeLocationForm
                    address={address}
                    coordinates={coordinates}
                    centerTrigger={centerTrigger}
                    onChange={(a, c) => { setAddress(a); setCoordinates(c); }}
                    onDirty={() => setLocationDirty(true)}
                />
                {error && name.trim() && <span style={{ color: 'red', fontSize: 13 }}>{error}</span>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Отмена
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading} disableElevation>
                    Сохранить
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditNodeDialog;
